import { Component, type ErrorInfo, type ReactNode } from "react";
import { isChunkLoadError } from "@/lib/lazy-with-retry";
import { RouteErrorFallback } from "@/app/RouteErrorFallback";

// Guard contra loop de reload: só auto-recarrega uma vez por janela curta.
const RELOAD_FLAG = "bo:chunk-reloaded-at";
const RELOAD_WINDOW_MS = 10_000;

function canAutoReload(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_FLAG) ?? 0);
    return !last || Date.now() - last > RELOAD_WINDOW_MS;
  } catch {
    return true; // sessionStorage indisponível — não bloqueia o reload
  }
}

function markReloaded(): void {
  try {
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    /* sessionStorage indisponível — segue sem o guard */
  }
}

type Props = {
  children: ReactNode;
  /** Mostrado enquanto o auto-reload de chunk dispara (evita piscar a UI de erro). */
  pendingFallback?: ReactNode;
};

type State = { error: Error | null; reloading: boolean };

/**
 * Error boundary das rotas lazy: um chunk que falha/trava não fica mais preso
 * no skeleton de Suspense indefinidamente. Falha de chunk → auto-reload único
 * (a nova carga pega chunks frescos); demais erros → UI de "recarregar".
 */
export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null, reloading: false };

  static getDerivedStateFromError(error: Error): State {
    return { error, reloading: isChunkLoadError(error) && canAutoReload() };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (isChunkLoadError(error) && canAutoReload()) {
      markReloaded();
      window.location.reload();
      return;
    }
    if (!isChunkLoadError(error)) {
      console.error("Route render failed:", error, info.componentStack);
    }
  }

  handleReload = (): void => {
    markReloaded();
    window.location.reload();
  };

  render(): ReactNode {
    const { error, reloading } = this.state;
    const { children, pendingFallback } = this.props;
    if (reloading) return pendingFallback ?? null;
    if (error) return <RouteErrorFallback onReload={this.handleReload} />;
    return children;
  }
}
